import React, { useEffect, useMemo, useState } from "react";
import "./Gallerytab.css";
import { AiOutlineArrowLeft, AiOutlineArrowRight, AiOutlineClose } from "react-icons/ai";
import ProgressiveImage from "./ProgressiveImage";

const imageSet = (folder, prefix, count) =>
  Array.from({ length: count }, (_, index) => ({
    name: `${prefix} photo ${index + 1}`,
    path: `/gallery/${folder}/${prefix}-${String(index + 1).padStart(2, "0")}.jpg`,
  }));

const localCategories = [
  {
    name: "EY Ripple",
    year: "2026",
    sourceUrl: "https://photos.app.goo.gl/yTLXUjGjypUcUk2z5",
    images: imageSet("ey-ripple-2026", "ey-ripple", 6),
  },
  {
    name: "PWC Industry Insight",
    year: "2026",
    sourceUrl: "https://siribrandstudiostz57.pixieset.com/pwc/",
    images: imageSet("pwc-2026", "pwc", 6),
  },
  {
    name: "Internal Auditors Event",
    year: "2026",
    sourceUrl: "https://siribrandstudiostz57.pixieset.com/internalauditorsevent/",
    images: imageSet("internal-auditors-event-2026", "internal-auditors", 6),
  },
  {
    name: "3rd Internal Audit Conference",
    year: "2026",
    sourceUrl: "https://photos.app.goo.gl/xLhG4v6BxmiHVxQWA",
    images: imageSet("internal-audit-conference-2026", "conference", 6),
  },
].map((category) => ({ ...category, thumbnail: category.images[0].path }));

const GallerySlider = () => {
  const [categories, setCategories] = useState(localCategories);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedYear, setSelectedYear] = useState("All");

  useEffect(() => {
    const fetchPreviousGallery = async () => {
      try {
        const response = await fetch("https://api.udiaa.or.tz/gallery_data");
        if (!response.ok) return;
        const data = await response.json();
        const previousCategories = data
          .filter((category) => Array.isArray(category.images) && category.images.length)
          .map((category) => ({
            ...category,
            year: category.name.match(/20\d{2}/)?.[0] || "Earlier",
            thumbnail: category.images[0].path,
          }))
          .filter((category) => !localCategories.some((local) => local.name === category.name));
        setCategories([...localCategories, ...previousCategories]);
      } catch (error) {
        console.info("Previous gallery albums are temporarily unavailable.");
      }
    };
    fetchPreviousGallery();
  }, []);

  const years = useMemo(
    () => ["All", ...Array.from(new Set(categories.map((category) => category.year)))],
    [categories]
  );
  const filteredCategories = selectedYear === "All"
    ? categories
    : categories.filter((category) => category.year === selectedYear);
  const activeCategory = categories.find((category) => category.name === selectedCategory);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setSelectedImage(null);
      if (!activeCategory || !selectedImage) return;
      if (event.key === "ArrowLeft" && selectedIndex > 0) {
        const newIndex = selectedIndex - 1;
        setSelectedIndex(newIndex);
        setSelectedImage(activeCategory.images[newIndex].path);
      }
      if (event.key === "ArrowRight" && selectedIndex < activeCategory.images.length - 1) {
        const newIndex = selectedIndex + 1;
        setSelectedIndex(newIndex);
        setSelectedImage(activeCategory.images[newIndex].path);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [activeCategory, selectedImage, selectedIndex]);

  const showImage = (image, index) => {
    setSelectedImage(image.path);
    setSelectedIndex(index);
  };

  const moveImage = (direction, event) => {
    event.stopPropagation();
    const newIndex = selectedIndex + direction;
    if (newIndex >= 0 && newIndex < activeCategory.images.length) {
      setSelectedIndex(newIndex);
      setSelectedImage(activeCategory.images[newIndex].path);
    }
  };

  return (
    <div className="gallery-container">
      {!selectedCategory && (
        <>
          <div className="gallery-toolbar" aria-label="Filter gallery by year">
            <span>Filter by year</span>
            <div className="year-filters">
              {years.map((year) => (
                <button key={year} type="button" className={selectedYear === year ? "active" : ""} onClick={() => setSelectedYear(year)}>
                  {year === "All" ? "All years" : year}
                </button>
              ))}
            </div>
          </div>
          <div className="category-grid">
            {filteredCategories.map((category) => (
              <button type="button" key={`${category.name}-${category.year}`} className="category-item" onClick={() => setSelectedCategory(category.name)}>
                <ProgressiveImage placeholder={category.thumbnail} src={category.thumbnail} alt={category.name} />
                <div className="category-name"><span>{category.name}</span><small>{category.year}</small></div>
              </button>
            ))}
          </div>
        </>
      )}

      {activeCategory && (
        <div>
          <button type="button" onClick={() => setSelectedCategory(null)} className="back-button"><AiOutlineArrowLeft /> All albums</button>
          <div className="gallery-heading-row">
            <h2 className="event-name">{activeCategory.name}</h2>
            {activeCategory.sourceUrl && <a href={activeCategory.sourceUrl} target="_blank" rel="noopener noreferrer" className="album-source-link">View full album</a>}
          </div>
          <div className="image-grid">
            {activeCategory.images.map((image, index) => (
              <img key={image.path} src={image.path} alt={image.name} loading="lazy" onClick={() => showImage(image, index)} />
            ))}
          </div>
        </div>
      )}

      {selectedImage && activeCategory && (
        <div className="modal" role="dialog" aria-modal="true" aria-label={`${activeCategory.name} photo`} onClick={() => setSelectedImage(null)}>
          <button type="button" className="modal-close" onClick={() => setSelectedImage(null)} aria-label="Close photo"><AiOutlineClose /></button>
          <img src={selectedImage} alt={`${activeCategory.name} ${selectedIndex + 1}`} onClick={(event) => event.stopPropagation()} />
          {selectedIndex > 0 && <AiOutlineArrowLeft className="arrow prev" onClick={(event) => moveImage(-1, event)} aria-label="Previous photo" />}
          {selectedIndex < activeCategory.images.length - 1 && <AiOutlineArrowRight className="arrow next" onClick={(event) => moveImage(1, event)} aria-label="Next photo" />}
        </div>
      )}
    </div>
  );
};

export default GallerySlider;
